import React, { useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, Search, Clock, User } from "lucide-react";
import { RiHomeFill } from "react-icons/ri";

const navItems = [
  { id: "menu", label: "Menu", icon: Menu, type: "button" },
  { id: "search", label: "Tìm kiếm", icon: Search, to: "/user/profile" },
  { id: "home", label: "Trang chủ", icon: RiHomeFill, to: "/" },
  {
    id: "xem-tiep",
    label: "Xem tiếp",
    icon: Clock,
    to: "/user/continue-watching",
  },
  { id: "dangnhap", label: "Đăng nhập", icon: User, type: "button1" },
];

const MobileNavbar = ({ openMenu, openLogin, closeMenu }) => {
  const navRef = useRef(null);
  const location = useLocation();
  const pathname = location.pathname;
  const [pressedItem, setPressedItem] = useState(null);

  const activeTab = navItems.find((item) =>
    pathname === "/"
      ? item.to === "/"
      : pathname.startsWith(item.to) && item.to !== "/"
  )?.id;

  const handleTouchStart = (itemId) => {
    setPressedItem(itemId);
  };

  const handleTouchEnd = () => {
    setTimeout(() => setPressedItem(null), 150);
  };

  return (
    <>
      {/* Backdrop gradient */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black/30 via-black/10 to-transparent pointer-events-none z-40" />

      <nav
        ref={navRef}
        className="sm:hidden fixed bottom-0 left-0 right-0 z-50 px-3 pb-3"
      >
        {/* Main container with advanced glassmorphism */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-slate-900/80 via-slate-800/85 to-slate-900/80 backdrop-blur-3xl ">
          {/* Animated background gradients */}
          <div className="absolute inset-0 bg-gradient-to-r from-orange-500/5 via-purple-500/5 to-blue-500/5 opacity-70" />
          <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent" />

          {/* Subtle animated border */}
          <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-orange-400/20 via-transparent to-purple-400/20 opacity-0 hover:opacity-100 transition-opacity duration-700" />

          <div className="relative grid grid-cols-5 max-w-md mx-auto px-2 py-3">
            {navItems.map((item) => {
              const IconComponent = item.icon;
              const isActive =
                item.id === "menu"
                  ? activeTab === "menu"
                  : pathname === item.to;
              const isPressed = pressedItem === item.id;

              const commonClass = `
  relative flex flex-col items-center justify-center h-18 
  transition-all duration-300 ease-out group 
  cursor-pointer rounded-2xl overflow-hidden 
  ${
    isPressed
      ? "scale-95"
      : isActive
      ? "scale-105 gap-5" // thêm gap-5 khi active
      : "hover:scale-102"
  }
  ${isActive ? "z-20" : "z-10"}
`;

              const iconWrapper = `
                relative transition-all duration-500 ease-out transform-gpu mb-2
                ${
                  isActive
                    ? "translate-y-[-8px] scale-125"
                    : isPressed
                    ? "translate-y-[-4px] scale-110"
                    : ""
                }
              `;

              const iconBgWrapper = `
                w-10 h-10 flex items-center justify-center rounded-2xl 
                transition-all duration-400 ease-out relative overflow-hidden
                transform-gpu shadow-lg
                ${
                  isActive
                    ? `bg-gradient-to-br from-orange-500 via-orange-400 to-amber-400 
                     shadow-orange-500/40 border border-orange-300/30 shadow-2xl`
                    : isPressed
                    ? `bg-gradient-to-br from-white/20 to-white/10 
                     shadow-white/20 border border-white/20`
                    : `bg-gradient-to-br from-white/8 to-white/4 
                     group-hover:from-white/15 group-hover:to-white/8
                     border border-white/10 group-hover:border-white/20 
                     group-hover:shadow-white/10`
                }
              `;

              const iconClass = `
                w-4 h-4 transition-all duration-300 relative z-10 drop-shadow-sm
                ${
                  isActive
                    ? "text-white"
                    : isPressed
                    ? "text-orange-300"
                    : "text-gray-300 group-hover:text-white"
                }
              `;

              const labelClass = `
  text-xs font-medium tracking-wide 
  transition-all duration-400 ease-out whitespace-nowrap
  ${
    isActive
      ? "opacity-100 text-orange-300 font-semibold drop-shadow-sm scale-105"
      : "opacity-100 text-gray-400 group-hover:opacity-90 group-hover:text-gray-200"
  }
`;

              // Enhanced ripple effect
              const RippleEffect = ({ isActive, isPressed }) => (
                <div className="absolute inset-0 overflow-hidden rounded-2xl">
                  {isPressed && (
                    <div className="absolute inset-0 bg-white/10 animate-pulse" />
                  )}
                  {isActive && (
                    <>
                      <div className="absolute inset-0 bg-gradient-to-tr from-white/20 via-white/10 to-transparent" />
                      <div className="absolute top-1 left-1 w-3 h-3 bg-white/30 rounded-full blur-sm animate-pulse" />
                    </>
                  )}
                </div>
              );

              if (item.type === "button") {
                return (
                  <button
                    key={item.id}
                    onClick={openMenu}
                    onTouchStart={() => handleTouchStart(item.id)}
                    onTouchEnd={handleTouchEnd}
                    onMouseDown={() => handleTouchStart(item.id)}
                    onMouseUp={handleTouchEnd}
                    onMouseLeave={handleTouchEnd}
                    className={commonClass}
                    style={{ WebkitTapHighlightColor: "transparent" }}
                  >
                    <div className={iconWrapper}>
                      <div className={iconBgWrapper}>
                        <RippleEffect
                          isActive={isActive}
                          isPressed={isPressed}
                        />
                        <IconComponent
                          className={iconClass}
                          strokeWidth={2.5}
                        />
                      </div>
                    </div>
                    <span className="relative inline-block w-max">
                      {isActive ? (
                        // Bóng cầu cam mờ bên dưới
                        <span
                          className="block w-10 h-2 rounded-full bg-orange-400 "
                          style={{ filter: "blur(10px)" }}
                        ></span>
                      ) : (
                        <span className={labelClass}>{item.label}</span>
                      )}
                    </span>
                  </button>
                );
              }

              if (item.type === "button1") {
                return (
                  <button
                    key={item.id}
                    onClick={openLogin}
                    onTouchStart={() => handleTouchStart(item.id)}
                    onTouchEnd={handleTouchEnd}
                    onMouseDown={() => handleTouchStart(item.id)}
                    onMouseUp={handleTouchEnd}
                    onMouseLeave={handleTouchEnd}
                    className={commonClass}
                    style={{ WebkitTapHighlightColor: "transparent" }}
                  >
                    <div className={iconWrapper}>
                      <div className={iconBgWrapper}>
                        <RippleEffect
                          isActive={isActive}
                          isPressed={isPressed}
                        />
                        <IconComponent
                          className={iconClass}
                          strokeWidth={2.5}
                        />
                      </div>
                    </div>
                    <span className="relative inline-block w-max">
                      {isActive ? (
                        // Bóng cầu cam mờ bên dưới
                        <span
                          className="block w-10 h-10 rounded-full bg-orange-400 opacity-60 blur-xl"
                          style={{ filter: "blur(10px)" }}
                        ></span>
                      ) : (
                        <span className={labelClass}>{item.label}</span>
                      )}
                    </span>
                  </button>
                );
              }

              return (
                <Link
                  key={item.id}
                  to={item.to}
                  onTouchStart={() => handleTouchStart(item.id)}
                  onTouchEnd={handleTouchEnd}
                  onMouseDown={() => handleTouchStart(item.id)}
                  onMouseUp={handleTouchEnd}
                  onMouseLeave={handleTouchEnd}
                  className={commonClass}
                  style={{ WebkitTapHighlightColor: "transparent" }}
                  onClick={closeMenu}
                >
                  <div className={iconWrapper}>
                    <div className={iconBgWrapper}>
                      <RippleEffect isActive={isActive} isPressed={isPressed} />
                      <IconComponent
                        className={iconClass}
                        strokeWidth={isActive ? 2.5 : 2}
                        fill={
                          isActive && (item.id === "home" || item.id === "user")
                            ? "currentColor"
                            : "none"
                        }
                      />
                    </div>
                  </div>
                  <span className="relative inline-block w-max">
                    {isActive ? (
                      <div className="relative flex flex-col items-center">
                        <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2">
                          <div className="w-16 h-6 rounded-ful"></div>
                          <div className="absolute inset-0 w-16 h-6 rounded-full bg-gradient-radial from-orange-500/80 via-orange-200/30 to-transparent blur-sm"></div>
                        </div>
                      </div>
                    ) : (
                      <span className={labelClass}>{item.label}</span>
                    )}
                  </span>
                </Link>
              );
            })}
          </div>

          {/* Bottom highlight line */}
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
        </div>
      </nav>
    </>
  );
};

export default MobileNavbar;
